// Currency formatting utilities for Kenyan Shillings (KSh)

/**
 * Format number as Kenyan Shillings currency
 * @param amount - The amount to format
 * @param showCents - Whether to show cents/decimal places (default: true)
 * @returns Formatted currency string
 */
export const formatKSh = (
  amount: number | string,
  showCents: boolean = true
): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return "KSh 0";
  }

  const formatter = new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });

  // Format the amount and replace KES with KSh for local convention
  return formatter.format(numAmount).replace("KES", "KSh");
};

/**
 * Format number with thousands separators without currency symbol
 * @param amount - The amount to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export const formatNumber = (
  amount: number | string,
  decimals: number = 2
): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return "0";
  }

  return new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numAmount);
};

/**
 * Format currency for display in tables and compact spaces
 * @param amount - The amount to format
 * @param compact - Whether to use compact notation for large numbers
 * @returns Formatted currency string
 */
export const formatCompactKSh = (
  amount: number | string,
  compact: boolean = false
): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return "KSh 0";
  }

  if (compact && numAmount >= 1000000) {
    return `KSh ${(numAmount / 1000000).toFixed(1)}M`;
  } else if (compact && numAmount >= 1000) {
    return `KSh ${(numAmount / 1000).toFixed(1)}K`;
  }

  return formatKSh(numAmount);
};

/**
 * Parse currency string to number
 * @param currencyString - String like "KSh 1,234.56" or "1,234.56"
 * @returns Parsed number or 0 if invalid
 */
export const parseCurrency = (currencyString: string): number => {
  if (!currencyString) return 0;

  // Remove currency symbols and spaces, keep only numbers, decimals, and commas
  const cleanString = currencyString.replace(/[^0-9.,]/g, "");

  // Handle different decimal separators (though Kenya typically uses .)
  const normalizedString = cleanString.replace(",", "");

  const parsed = parseFloat(normalizedString);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Get currency symbol only
 * @returns Currency symbol string
 */
export const getCurrencySymbol = (): string => {
  return "KSh";
};

/**
 * Format amount for input fields (without currency symbol)
 * @param amount - The amount to format
 * @returns Formatted string for input
 */
export const formatForInput = (amount: number | string): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    return "";
  }

  return numAmount.toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Export default format function
export default formatKSh;
